import { useState, useCallback, useEffect } from 'react';
import { useFilterMap, useMasterdata, useProjectsFilter, useUserCapabilities } from '../mutations';
import { MasterDataHookReturn } from './useDisplayField';
import { useQuery } from '@tanstack/react-query';
import { qk } from '../lib';
import { getMe } from '../features/auth';
import type { MasterData } from '../service';


export const useMasterData = (): MasterDataHookReturn => {

  const { data: session } = useQuery({
    queryKey: qk.me(),
    queryFn: getMe,
  });

  const masterDataService = useMasterdata();
  const capabilitiesService = useUserCapabilities();
  const filterMapService = useFilterMap();
  const projectFilters = useProjectsFilter();

  const [masterData, setMasterData] = useState<MasterData[]>([]);
  const [capabilitiesData, setCapabilitiesData] = useState<String[]>([]);
  const [filterMapData, setFilterMapData] = useState<any>([]);
  const [projectsFilterData, setProjectsFilterData] = useState<any>([]);

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const fetchMasterData = useCallback(async (): Promise<void> => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Fetch all master data in parallel to significantly reduce loading time
      const [data, capsData, filterMap, projFilters] = await Promise.all([
        masterDataService.mutateAsync(),
        capabilitiesService.mutateAsync(),
        filterMapService.mutateAsync(),
        projectFilters.mutateAsync(),
      ]);

      setMasterData(data);
      setCapabilitiesData(capsData);
      setFilterMapData(filterMap);
      setProjectsFilterData(projFilters);
    } catch (err) {
      const error = err as Error;
      setError(error.message || 'Failed to fetch master data');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const refetch = useCallback(async (): Promise<void> => {
    await fetchMasterData();
  }, [fetchMasterData]);

  const clearMasterData = useCallback(() => {
    setMasterData([]);
    setCapabilitiesData([]);
    setFilterMapData([]);
    setProjectsFilterData([]);
  }, []);

  useEffect(() => {
    if (!session?.authenticated) {
      clearMasterData();
    }
  }, [session?.authenticated, clearMasterData]);

  useEffect(() => {
    if (session?.authenticated) {
      fetchMasterData();
    }
  }, [session?.authenticated]);

  return {
    masterData,
    capabilitiesData,
    filterMapData,
    projectsFilterData,
    isLoading,
    error,
    refetch,
    clearError,
    clearMasterData,
  };

};

export default useMasterData;
